import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Cloud, Loader2, Play, Download, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AdminBackup = () => {
  const me = JSON.parse(localStorage.getItem('user') || '{}');
  const [meta, setMeta] = useState(null);
  const [backups, setBackups] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, histRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/backup/list?admin_id=${me.id}`),
        fetch(`${API_BASE}/api/admin/backup/history?admin_id=${me.id}`),
      ]);
      if (listRes.ok) {
        const d = await listRes.json();
        setMeta({ bucket: d.bucket, region: d.region, retention_days: d.retention_days });
        setBackups(d.backups || []);
      }
      if (histRes.ok) setHistory(await histRes.json());
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [me.id]);

  useEffect(() => {
    if (me.is_admin) loadAll();
  }, [me.is_admin, loadAll]);

  const triggerBackup = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/backup/run?admin_id=${me.id}`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur backup');
      }
      const data = await res.json();
      toast.success(`Backup réussi (${data.size_human})`);
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Backup échoué');
    } finally {
      setRunning(false);
    }
  };

  const downloadBackup = async (key) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/backup/download-url?admin_id=${me.id}&key=${encodeURIComponent(key)}`
      );
      if (!res.ok) throw new Error('Impossible de générer l\'URL');
      const { url } = await res.json();
      window.open(url, '_blank');
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!me.is_admin) {
    return (
      <div className="p-8" data-testid="admin-backup-no-access">
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldCheck className="mx-auto mb-4 text-muted-foreground" size={36} />
            <p className="text-muted-foreground">Accès réservé aux administrateurs.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="admin-backup-page">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight flex items-center gap-3">
          <Cloud className="text-[#205F79]" size={32} />
          Sauvegardes
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          Backup MongoDB → AWS S3, exécuté automatiquement chaque jour à 02:00 UTC.
        </p>
      </div>

      {/* Meta info */}
      {meta && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Bucket S3</p>
                <p className="font-mono font-medium">{meta.bucket}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Région</p>
                <p className="font-mono font-medium">{meta.region}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Rétention</p>
                <p className="font-mono font-medium">{meta.retention_days} jours</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual trigger */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Sauvegarde manuelle</CardTitle>
              <CardDescription>Déclenche un backup immédiat de toutes les collections MongoDB</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={loadAll}
                disabled={loading}
                data-testid="btn-refresh-backups"
                className="gap-2"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Rafraîchir
              </Button>
              <Button
                onClick={triggerBackup}
                disabled={running}
                data-testid="btn-run-backup"
                className="gap-2 bg-[#205F79] hover:bg-[#184a5e]"
              >
                {running ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sauvegarde en cours…
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Lancer un backup
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Backup list */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Backups disponibles sur S3</CardTitle>
          <CardDescription>{backups.length} fichier(s) — Les plus récents en premier</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">Chargement…</div>
          ) : backups.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground" data-testid="backup-empty-state">
              <AlertTriangle className="mx-auto mb-3 text-amber-500" size={28} />
              Aucun backup disponible. Cliquez sur "Lancer un backup" pour en créer un.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-3 pr-4">Fichier</th>
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Taille</th>
                    <th className="py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((b) => (
                    <tr key={b.key} className="border-b border-border last:border-0" data-testid={`backup-row-${b.key}`}>
                      <td className="py-3 pr-4 font-mono text-xs">{b.key.replace('mongodb/', '')}</td>
                      <td className="py-3 pr-4">{new Date(b.last_modified).toLocaleString('fr-FR')}</td>
                      <td className="py-3 pr-4 tabular-nums">{b.size_human}</td>
                      <td className="py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadBackup(b.key)}
                          data-testid={`btn-download-${b.key}`}
                          className="gap-2"
                        >
                          <Download size={14} />
                          Télécharger
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle>Historique d'exécution</CardTitle>
          <CardDescription>20 dernières opérations (succès et échecs)</CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="py-6 text-center text-muted-foreground text-sm">Aucun historique disponible.</p>
          ) : (
            <div className="space-y-2">
              {history.slice(0, 20).map((h) => (
                <div
                  key={h.id}
                  className="flex items-center justify-between p-3 border border-border rounded-md text-sm"
                  data-testid={`history-row-${h.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        h.status === 'success' ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    />
                    <div>
                      <p className="font-medium">
                        {h.trigger_type === 'scheduled' ? 'Planifié' : 'Manuel'} —{' '}
                        <span className="text-muted-foreground">{h.status}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(h.created_at).toLocaleString('fr-FR')}
                        {h.size_human && ` • ${h.size_human}`}
                        {h.error && ` • ${h.error}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBackup;
